import React, { FC, useCallback } from 'react';
import { useFormikContext } from 'formik';
import { Column, FormInput, Row } from 'Shared/components';
import { formatPhoneNumber } from 'Shared/utils';
import {
  ContactInfoFieldsType,
  FormValuesType,
} from 'QuoteApp/services/endpoints/GetDetails';
import { getFieldErrors } from 'QuoteApp/utils';
import { useFieldData } from 'QuoteApp/utils/contexts';

type ContactInfoType = {
  isEditing: boolean;
  editable: {
    email: boolean;
    phoneNumber: boolean;
    phoneType: boolean;
  };
};

export const ContactInfo: FC<ContactInfoType> = ({ isEditing, editable }) => {
  const {
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    touched,
    errors,
    initialValues,
  } = useFormikContext<FormValuesType>();
  const { serverErrors } = useFieldData();

  const getErrorMessage = useCallback(
    (fieldName: string) =>
      getFieldErrors<FormValuesType>(
        fieldName,
        true,
        true,
        errors,
        serverErrors,
        values,
        touched,
        initialValues
      ),
    [errors, touched, values, serverErrors, initialValues]
  );

  const getContactInfoInputProps = useCallback(
    (fieldName: keyof ContactInfoFieldsType) => {
      const isEditableField = isEditing && editable[fieldName];
      const fullFieldName = `contactInfo.${values.primaryDriverId}.${fieldName}`;

      return {
        name: fullFieldName,
        onChange: handleChange,
        onBlur: handleBlur,
        value: values.contactInfo[values.primaryDriverId][fieldName],
        errorMessage: getErrorMessage(fullFieldName),
        disabled: !isEditableField,
      };
    },
    [
      isEditing,
      editable,
      getErrorMessage,
      handleBlur,
      handleChange,
      values.contactInfo,
      values.primaryDriverId,
    ]
  );

  return (
    <>
      <Row>
        <Column variant="third">
          <FormInput
            {...getContactInfoInputProps('phoneNumber')}
            data-testid="phone-number"
            variant={!isEditing ? 'readOnly' : 'text'}
            label="Phone Number"
            maxLength={12}
            value={formatPhoneNumber(
              values.contactInfo[values.primaryDriverId].phoneNumber
            )}
          />
        </Column>
        <Column variant="third">
          <FormInput
            {...getContactInfoInputProps('phoneType')}
            data-testid="is-mobile-number"
            variant={!isEditing ? 'readOnly' : 'radioButtonGroup'}
            options={{ mobile: 'Yes', home: 'No' }}
            label="Is this a mobile number?"
            onChange={(value: string) =>
              setFieldValue(
                `contactInfo.${values.primaryDriverId}.phoneType`,
                value
              )
            }
          />
        </Column>
        <Column variant="third">
          <FormInput
            {...getContactInfoInputProps('email')}
            data-testid="email-address"
            variant={!isEditing ? 'readOnly' : 'text'}
            label="Email Address"
          />
        </Column>
      </Row>
    </>
  );
};
